import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuthContext } from "../Contexts/AuthContext";
import Navbar from "../Components/Navbar";
import loader from "../assets/loading.gif";

const API = process.env.REACT_APP_BASE_URL;

const authHeaders = () => ({
  headers: { "Content-Type": "application/json", token: Cookies.get("token") },
});

const fmtEs = (n, d = 2) =>
  Number(n || 0).toLocaleString("es-ES", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return String(v); // legacy stores a preformatted string
  return d.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Payout-track colours. Same vocabulary as the legacy Order collection, so
 * historical rows land in the right bucket without translation.
 */
const statusTone = (s) => {
  const v = String(s || "").toUpperCase();
  if (v === "COMPLETADA") return "ok";
  if (v === "ANULADA") return "bad";
  if (v === "PENDIENTE") return "wait";
  return "info"; // Recibido, En Proceso
};

/** What the customer should do next, if anything. */
const nextAction = (o) => {
  if (o.source !== "pmi") return null;
  if (o.raw.paid) return null;
  if (o.raw.checkoutUrl) return "pay";
  return null;
};

const OrderHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const [pmiOrders, setPmiOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");

  /* ---------------------------------------------------------------- *
   * PMI orders only. The legacy `Order` collection is still populated but
   * belongs to a flow that no longer runs, and showing both left the customer
   * portal disagreeing with the admin dashboard.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    window.scrollTo(0, 0);

    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/pmi/orders`, authHeaders());
        setPmiOrders(data?.orders || []);
      } catch (err) {
        console.warn("PMI orders unavailable", err?.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const orders = useMemo(
    () =>
      pmiOrders.map((o) => ({
        key: o._id,
        source: "pmi",
        ref: o.reference,
        date: o.createdAt,
        bname: o.beneficiary?.bname,
        status: o.status,
        sent: o.gross,
        currencyFrom: o.currencyFrom || "EUR",
        receives: o.receives,
        currencyTo: o.currencyTo || "VES",
        raw: o,
      })),
    [pmiOrders]
  );

  const shown = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "open")
      return orders.filter((o) =>
        ["Pendiente", "Recibido", "En Proceso"].includes(o.status)
      );
    return orders.filter((o) => o.status === "Completada");
  }, [orders, filter]);

  /* ---------------------------------------------------------------- *
   * Actions
   * ---------------------------------------------------------------- */

  /**
   * Opening the detail silently re-polls PayTKS for that order, so the customer
   * always sees current state without being offered a button that looks like
   * they can change it. Status is only ever set by the webhook or by an admin.
   */
  useEffect(() => {
    if (!detail || detail.source !== "pmi") return;
    let alive = true;

    (async () => {
      try {
        setRefreshing(true);
        const { data } = await axios.get(
          `${API}/api/pmi/order/${detail.ref}`,
          authHeaders()
        );
        if (!alive || !data?.order) return;
        setPmiOrders((prev) =>
          prev.map((o) => (o.reference === detail.ref ? data.order : o))
        );
        setDetail((d) =>
          d && d.ref === detail.ref
            ? { ...d, raw: data.order, status: data.order.status }
            : d
        );
      } catch (err) {
        // Non-fatal — we already have the copy from the list
        console.warn("status refresh failed", err?.message);
      } finally {
        if (alive) setRefreshing(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.ref]);

  if (!user) return <Navigate to={"/login"} />;

  /* ---------------------------------------------------------------- *
   * Render
   * ---------------------------------------------------------------- */

  return (
    <div className="Orderhistory tv-hist">
      <style>{`
        .tv-hist {
          --accent: #F6394F;
          --accent-strong: #e22440;
          --ink: #101828;
          --muted: #667085;
          --label: #475467;
          --field: #f5f6f8;
          --line: #ececf0;
          color: var(--ink);
        }
        .tv-hist * { box-sizing: border-box; }

        .tv-h-wrap { max-width: 1180px; margin: 0 auto; margin-top: 50px; padding: 32px 20px 64px; }
        @media (max-width: 700px) { .tv-h-wrap { padding: 20px 16px 48px; } }

        .tv-h-head {
          display: flex; justify-content: space-between; align-items: flex-end;
          gap: 16px; flex-wrap: wrap; margin-bottom: 20px;
        }
        .tv-h-title { font-size: 1.6rem; font-weight: 700; margin: 0 0 4px; letter-spacing: -.02em; }
        .tv-h-sub { color: var(--muted); font-size: .92rem; margin: 0; }

        .tv-tabs { display: flex; background: var(--field); border-radius: 10px; padding: 4px; gap: 4px; }
        .tv-tabs button {
          border: none; background: none; padding: 8px 16px; border-radius: 7px;
          font-size: .85rem; font-weight: 600; color: var(--muted); cursor: pointer;
          transition: all .15s; white-space: nowrap;
        }
        .tv-tabs button.on { background: #fff; color: var(--accent); box-shadow: 0 1px 3px rgba(16,24,40,.12); }

        /* one row per order — a card, not a table cell, so it works on phones */
        .tv-o {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 4px 16px;
          background: #fff;
          border: 1px solid var(--line);
          border-radius: 12px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          transition: border-color .15s, box-shadow .15s;
        }
        .tv-o:hover { border-color: #d0d5dd; box-shadow: 0 2px 10px rgba(16,24,40,.05); }
        .tv-o-ref { font-weight: 700; font-size: .95rem; font-variant-numeric: tabular-nums; }
        .tv-o-meta { font-size: .82rem; color: var(--muted); }
        .tv-o-amt {
          text-align: right; font-weight: 700; font-size: .95rem;
          font-variant-numeric: tabular-nums; white-space: nowrap;
        }
        .tv-o-get { text-align: right; font-size: .82rem; color: var(--muted); font-variant-numeric: tabular-nums; }

        .tv-badge {
          display: inline-block; padding: 3px 10px; border-radius: 999px;
          font-size: .72rem; font-weight: 700; letter-spacing: .02em;
        }
        .tv-badge.ok   { background: #e7f8f0; color: #027a48; }
        .tv-badge.bad  { background: #fef3f2; color: #b42318; }
        .tv-badge.wait { background: #fffaeb; color: #b54708; }
        .tv-badge.info { background: #eff8ff; color: #175cd3; }

        .tv-empty-box {
          text-align: center; padding: 56px 20px;
          border: 1px dashed var(--line); border-radius: 14px; background: #fff;
        }

        .tv-btn {
          background: var(--accent); color: #fff; border: none; border-radius: 9px;
          padding: 9px 18px; font-size: .87rem; font-weight: 700; cursor: pointer;
          transition: background .15s;
        }
        .tv-btn:hover:not(:disabled) { background: var(--accent-strong); }
        .tv-btn:disabled { opacity: .55; cursor: not-allowed; }
        .tv-btn.ghost { background: #fff; color: var(--label); border: 1px solid #d0d5dd; }
        .tv-btn.ghost:hover { background: var(--field); }
        .tv-btn.danger { background: #fff; color: #b42318; border: 1px solid #fda29b; }

        /* detail */
        .Orderhistory .modal-dialog.tv-dlg { max-width: 580px !important; width: 100% !important; }
        .tv-dlg .modal-content { border: none; border-radius: 14px; box-shadow: 0 16px 48px rgba(16,24,40,.2); }
        .tv-dlg .modal-header { padding: 16px 20px; border-bottom: 1px solid var(--line); }
        .tv-dlg .modal-body { padding: 20px; max-height: 66vh; overflow-y: auto; }
        .tv-dlg .modal-footer { padding: 14px 20px; border-top: 1px solid var(--line); }
        @media (max-width: 520px) {
          .Orderhistory .modal-dialog.tv-dlg { max-width: 92% !important; }
        }

        .tv-row { display: flex; justify-content: space-between; gap: 16px; font-size: .87rem; padding: 7px 0; }
        .tv-row span { color: var(--muted); }
        .tv-row b { color: var(--ink); text-align: right; font-variant-numeric: tabular-nums; word-break: break-word; }
        .tv-row.total { border-top: 1px solid var(--line); margin-top: 8px; padding-top: 12px; font-size: .95rem; }

        .tv-sec {
          font-size: .72rem; font-weight: 700; letter-spacing: .08em;
          text-transform: uppercase; color: var(--muted);
          margin: 20px 0 6px; padding-bottom: 6px; border-bottom: 1px solid var(--line);
        }
        .tv-sec:first-child { margin-top: 0; }

        /* timeline */
        .tv-tl { list-style: none; padding: 0; margin: 8px 0 0; }
        .tv-tl li { display: flex; gap: 12px; padding-bottom: 14px; position: relative; }
        .tv-tl li:not(:last-child)::before {
          content: ""; position: absolute; left: 4px; top: 14px; bottom: 0;
          width: 1px; background: var(--line);
        }
        .tv-dot {
          width: 9px; height: 9px; border-radius: 50%; background: var(--accent);
          margin-top: 4px; flex-shrink: 0; z-index: 1;
        }
        .tv-dot.pmi { background: #98a2b3; }
        .tv-tl-body { font-size: .84rem; }
        .tv-tl-when { font-size: .74rem; color: #98a2b3; }

        @media (prefers-reduced-motion: reduce) {
          .tv-hist *, .tv-hist *::before, .tv-hist *::after { transition: none !important; }
        }
      `}</style>

      <Navbar />

      <div className="tv-h-wrap">
        <div className="tv-h-head">
          <div>
            <h1 className="tv-h-title">Historial de órdenes</h1>
            <p className="tv-h-sub">
              {orders.length === 0
                ? "Aquí verás todos tus envíos."
                : `${orders.length} ${orders.length === 1 ? "orden" : "órdenes"}`}
            </p>
          </div>

          {orders.length > 0 && (
            <div className="tv-tabs">
              <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")}>
                Todas
              </button>
              <button className={filter === "open" ? "on" : ""} onClick={() => setFilter("open")}>
                En curso
              </button>
              <button className={filter === "done" ? "on" : ""} onClick={() => setFilter("done")}>
                Completadas
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <img src={loader} alt="Cargando" className="loader" />
        ) : shown.length === 0 ? (
          <div className="tv-empty-box">
            <p style={{ color: "var(--muted)", marginBottom: 18 }}>
              {orders.length === 0
                ? "Todavía no has realizado ningún envío."
                : "No hay órdenes en esta categoría."}
            </p>
            {orders.length === 0 && (
              <button className="tv-btn" onClick={() => navigate("/prealertar")}>
                Enviar dinero
              </button>
            )}
          </div>
        ) : (
          shown.map((o) => (
            <div className="tv-o" key={o.key} onClick={() => setDetail(o)}>
              <div>
                <div className="tv-o-ref">
                  <span className="notranslate">{o.ref}</span>
                </div>
                <div className="tv-o-meta">
                  {fmtDate(o.date)}
                  {o.bname ? ` · ${o.bname}` : ""}
                </div>
                <div style={{ marginTop: 6 }}>
                  <span className={`tv-badge ${statusTone(o.status)}`}>
                    {String(o.status || "—").toUpperCase()}
                  </span>
                  {nextAction(o) === "pay" && (
                    <span className="tv-badge wait" style={{ marginLeft: 6 }}>
                      PAGO PENDIENTE
                    </span>
                  )}
                </div>
              </div>

              <div>
                <div className="tv-o-amt">
                  {typeof o.sent === "number" ? fmtEs(o.sent) : o.sent || "—"}{" "}
                  {o.currencyFrom}
                </div>
                <div className="tv-o-get">
                  →{" "}
                  {typeof o.receives === "number"
                    ? fmtEs(o.receives)
                    : o.receives || "—"}{" "}
                  {o.currencyTo}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ============ detail ============ */}
      {detail && (
        <div
          className="modal_bg d-flex justify-content-center align-items-center"
          style={{ position: "fixed", inset: 0, zIndex: 9999 }}
        >
          <div className="modal d-block" tabIndex="-1" style={{ position: "relative" }}>
            <div className="modal-dialog tv-dlg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title fs-5">
                    Orden <span className="notranslate">{detail.ref}</span>
                  </h1>
                  <button type="button" className="btn-close" onClick={() => setDetail(null)} />
                </div>

                <div className="modal-body">
                  <p className="tv-sec">
                    Estado
                    {refreshing && (
                      <span
                        style={{
                          float: "right",
                          fontWeight: 400,
                          textTransform: "none",
                          letterSpacing: 0,
                          color: "#98a2b3",
                        }}
                      >
                        comprobando…
                      </span>
                    )}
                  </p>
                  <div className="tv-row">
                    <span>Tu envío</span>
                    <b>
                      <span className={`tv-badge ${statusTone(detail.status)}`}>
                        {String(detail.status || "—").toUpperCase()}
                      </span>
                    </b>
                  </div>
                  {detail.source === "pmi" && (
                    <div className="tv-row">
                      <span>Pago</span>
                      <b>{detail.raw.paid ? "Confirmado" : "Pendiente"}</b>
                    </div>
                  )}
                  {detail.status === "Anulada" && (
                    <p
                      style={{
                        fontSize: ".82rem",
                        color: "var(--muted)",
                        margin: "10px 0 0",
                      }}
                    >
                      ¿Dudas con esta orden?{" "}
                      <a
                        href="https://wa.me/34620818181"
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "var(--accent)", fontWeight: 600 }}
                      >
                        Contacta con Atención al Cliente
                      </a>
                      .
                    </p>
                  )}

                  {detail.raw.payoutRef && (
                    <div className="tv-row">
                      <span>Referencia del pago</span>
                      <b>{detail.raw.payoutRef}</b>
                    </div>
                  )}

                  <p className="tv-sec">Beneficiario</p>
                  <div className="tv-row">
                    <span>Nombre</span>
                    <b>{detail.bname || "—"}</b>
                  </div>
                  <div className="tv-row">
                    <span>Cédula</span>
                    <b>{detail.raw.beneficiary?.cedula || detail.raw.cedula || "—"}</b>
                  </div>
                  <div className="tv-row">
                    <span>Banco</span>
                    <b>{detail.raw.beneficiary?.bbank || detail.raw.bbank || "—"}</b>
                  </div>
                  <div className="tv-row">
                    <span>Cuenta</span>
                    <b>
                      {detail.raw.beneficiary?.bAccnumber || detail.raw.bAccnumber || "—"}
                    </b>
                  </div>

                  <p className="tv-sec">Importe</p>
                  {detail.source === "pmi" && (
                    <>
                      <div className="tv-row">
                        <span>Comisión</span>
                        <b>{fmtEs(detail.raw.fee)} €</b>
                      </div>
                      <div className="tv-row">
                        <span>Tasa aplicada</span>
                        <b>
                          1 EUR = {fmtEs(detail.raw.fxRate, 4)} {detail.currencyTo}
                        </b>
                      </div>
                    </>
                  )}
                  <div className="tv-row">
                    <span>Recibe</span>
                    <b>
                      {typeof detail.receives === "number"
                        ? fmtEs(detail.receives)
                        : detail.receives}{" "}
                      {detail.currencyTo}
                    </b>
                  </div>
                  <div className="tv-row total">
                    <span>Pagado</span>
                    <b>
                      {typeof detail.sent === "number" ? fmtEs(detail.sent) : detail.sent}{" "}
                      {detail.currencyFrom}
                    </b>
                  </div>

                  {detail.raw.statusHistory?.length > 0 && (
                    <>
                      <p className="tv-sec">Seguimiento</p>
                      <ul className="tv-tl">
                        {[...detail.raw.statusHistory]
                          .reverse()
                          .map((h, i) => (
                            <li key={i}>
                              <span className={`tv-dot ${h.track === "pmi" ? "pmi" : ""}`} />
                              <div className="tv-tl-body">
                                <div style={{ fontWeight: 600 }}>
                                  {h.track === "pmi" ? "Pago: " : ""}
                                  {h.status}
                                </div>
                                <div className="tv-tl-when">
                                  {fmtDate(h.at)}
                                  {h.note ? ` · ${h.note}` : ""}
                                </div>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  {/* The only action a customer has is paying. Anulaciones and
                      payout status are handled by the admin. */}
                  {nextAction(detail) === "pay" && (
                    <button
                      className="tv-btn me-auto"
                      onClick={() => {
                        window.location.href = detail.raw.checkoutUrl;
                      }}
                    >
                      Pagar ahora
                    </button>
                  )}

                  <button className="tv-btn ghost" onClick={() => setDetail(null)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;